import { Target, UsersThree, Megaphone, ShieldWarning } from "@phosphor-icons/react";

const ddmmyyyy = (iso) => {
  const d = iso ? new Date(iso) : new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()}`;
};

const Section = ({ title, icon: Icon, children }) => (
  <div className="bg-white border border-slate-200 rounded-sm">
    <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
      <Icon size={18} weight="duotone" className="text-teal-700" />
      <h3 className="text-sm font-semibold tracking-wide uppercase text-teal-700">{title}</h3>
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const Field = ({ label, value }) => (
  <div>
    <p className="text-xs font-semibold tracking-[0.08em] uppercase text-slate-400 mb-1">{label}</p>
    <p className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">{value || <span className="text-slate-300">—</span>}</p>
  </div>
);

const NumberedList = ({ label, items, prefix }) => (
  <div>
    <p className="text-xs font-semibold tracking-[0.08em] uppercase text-slate-400 mb-2">{label}</p>
    <ol className="space-y-2">
      {items.map((v, i) => (
        <li key={i} className="flex items-start gap-2.5">
          <span className="mt-0.5 w-6 h-6 shrink-0 rounded-sm bg-teal-50 border border-teal-200 text-teal-700 font-mono text-xs font-bold flex items-center justify-center">{prefix}{i + 1}</span>
          <span className="text-sm text-slate-800 leading-relaxed">{v || <span className="text-slate-300">—</span>}</span>
        </li>
      ))}
    </ol>
  </div>
);

export default function CommunicationBlueprint({ doc }) {
  const p = doc.payload || {};
  const objectives = (p.objectives || ["", "", ""]).slice(0, 3);
  const keyMessages = (p.keyMessages || ["", "", ""]).slice(0, 3);
  const risks = (p.risks || []).filter((r) => (r.risk && r.risk.trim()) || (r.control && r.control.trim()));

  return (
    <div className="space-y-4" data-testid="communication-blueprint">
      <div className="bg-white border border-slate-200 rounded-sm px-5 py-3 flex items-center justify-between">
        <span className="font-mono text-xs uppercase tracking-[0.1em] text-slate-400">Communication & Engagement Blueprint</span>
        <span className="font-mono text-xs text-slate-600" data-testid="blueprint-date">Date: {ddmmyyyy(doc.updated_at || doc.created_at)}</span>
      </div>

      <Section title="Strategic Foundations" icon={Target}>
        <div className="space-y-5">
          <Field label="Business Goal(s)" value={p.businessGoals} />
          <NumberedList label="Communication Objectives" items={objectives} prefix="O" />
        </div>
      </Section>

      <Section title="Stakeholder & Channel Mapping" icon={UsersThree}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field label="Internal Stakeholders" value={p.internalStakeholders} />
          <Field label="External Stakeholders" value={p.externalStakeholders} />
        </div>
        <div className="mt-5"><Field label="Channels" value={p.channels} /></div>
      </Section>

      <Section title="Strategy & Core Messaging" icon={Megaphone}>
        <div className="space-y-5">
          <Field label="Strategy & Methodology" value={p.strategy} />
          <NumberedList label="Key Messages" items={keyMessages} prefix="M" />
        </div>
      </Section>

      <Section title="Threat & Risk Mitigation" icon={ShieldWarning}>
        {risks.length === 0 ? (
          <p className="text-sm text-slate-300">— No risks recorded —</p>
        ) : (
          <div className="border border-slate-200 rounded-sm overflow-hidden" data-testid="communication-risk-table">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-2.5 font-semibold w-1/2">Risk to Mitigate</th>
                  <th className="px-4 py-2.5 font-semibold w-1/2">Defensive Control</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {risks.map((r, i) => (
                  <tr key={i}>
                    <td className="px-4 py-3 text-sm text-slate-800 align-top">{r.risk || <span className="text-slate-300">—</span>}</td>
                    <td className="px-4 py-3 text-sm text-slate-700 align-top">{r.control || <span className="text-slate-300">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </div>
  );
}
